/** "Deleted · Undo": a short-lived bar at the bottom of every page, shown after a task is deleted. */
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { RotateCcw, X } from "lucide-react";
import { useEffect, useState } from "react";
import { post } from "../lib/api";

type Undo = { id: string; label: string };
let current: Undo | null = null;
const listeners = new Set<(u: Undo | null) => void>();
const set = (u: Undo | null) => { current = u; listeners.forEach((l) => l(u)); };
export const offerUndo = (u: Undo) => set(u);

export function UndoBar() {
  const [u, setU] = useState<Undo | null>(current);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => { listeners.add(setU); return () => { listeners.delete(setU); }; }, []);
  useEffect(() => { if (!u) return; const t = setTimeout(() => set(null), 12000); return () => clearTimeout(t); }, [u]);
  if (!u) return null;
  const undo = async () => {
    setBusy(true);
    try {
      await post(`/api/work/items/${u.id}/restore`, {});
      await qc.invalidateQueries();
      set(null);
      void navigate({ to: "/work/$id", params: { id: u.id } });
    } finally { setBusy(false); }
  };
  return (
    <div role="status" className="fixed bottom-20 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-xl bg-[var(--text)] px-4 py-2.5 text-sm text-[var(--bg)] shadow-lg md:bottom-6">
      <span className="max-w-[60vw] truncate">Deleted {u.label}</span>
      <button className="inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline" disabled={busy} onClick={undo}><RotateCcw size={14} /> Undo</button>
      <button aria-label="Close" className="opacity-70 hover:opacity-100" onClick={() => set(null)}><X size={14} /></button>
    </div>
  );
}
